'use client'

import { useId, useRef } from 'react'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery, Grid, Box, Typography } from '@mui/material'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

// Types
import { CardProps } from '@/types/components/molecules/items/card'

// Helpers
import { useTranslation } from '@/helpers/i18n/client'

const CardMoelcule = (props: CardProps) => {
	// Props
	const { lng, title, description, icon, reverse } = props

	// Variables
	const { t } = useTranslation(lng)
	const muiTheme = useTheme()
	const patterId = useId()
	const gridRef = useRef<HTMLDivElement>(null)
	const greaterThanMedium = useMediaQuery(muiTheme.breakpoints.up('md'))
	const greaterThanLarge = useMediaQuery(muiTheme.breakpoints.up('lg'))
	const width = 100
	const height = 120
	const radius = 20
	const margin = 20

	// GSAP
	useGSAP(
		() => {
			gsap.timeline({
				scrollTrigger: {
					trigger: gridRef.current,
					start: 'top bottom',
					end: 'bottom top',
					// markers: true,
					onUpdate: self => {
						gsap.set('pattern', {
							attr: { x: `${self.progress * 400}%` }
						})
					}
				}
			})
		},
		{ scope: gridRef }
	)

	// Callbacks
	const generatePath = (padding: number, hover?: boolean) => {
		return (
			`M ${padding} ${radius + margin} ` +
			`Q ${padding} ${margin + padding} ${radius} ${(!hover ? margin : (margin * 5) / 6) + padding} ` +
			`L ${width - radius} ${(!hover ? margin : margin / 2.5) + padding} ` +
			`Q ${width - padding} ${(!hover ? margin : margin / 4) + padding} ${width - padding}  ${radius + (!hover ? margin : 0)}` +
			`L ${width - padding} ${height - radius}` +
			`Q ${width - padding} ${height - padding} ${width - radius} ${height - padding}` +
			`L ${radius} ${height - padding}` +
			`Q ${padding} ${height - padding} ${padding} ${height - radius}` +
			`Z`
		)
	}

	return (
		<Grid
			ref={gridRef}
			minWidth={260}
			sx={{
				...(reverse ? { mt: 30 } : {}),
				'&:hover': {
					cursor: 'pointer',
					'& .icon': { transform: 'skew(-5deg)' },
					'& .content path:nth-child(2)': {
						fill: '#ff9191',
						d: `path("${generatePath(0, true)}")`
					},
					'& .content path:nth-child(3)': {
						opacity: 1,
						d: `path("${generatePath(5, true)}")`
					}
				}
			}}
		>
			<Grid container direction={reverse ? 'column-reverse' : 'column'} spacing={2}>
				<Grid size={12} display="inline-flex" justifyContent="center" zIndex={1} sx={{ ...(reverse ? { mt: -6.25, mb: 6.25 } : { mt: 6.25, mb: -6.25 }) }}>
					<Box
						className="icon"
						width={100}
						height={100}
						bgcolor="common.white"
						borderRadius={7}
						sx={{
							p: 3,
							transition: 'all 0.3s ease',
							'& .MuiSvgIcon-root': {
								fontSize: 52,
								color: '#070f2b'
							}
						}}
					>
						{icon}
					</Box>
				</Grid>

				<Grid
					className="content"
					size={12}
					position="relative"
					sx={{
						'& svg': {
							...(reverse ? { mb: '-20%', transform: 'rotate(180deg)' } : { mt: '-20%' })
						},
						'& path': { transition: 'all 0.3s ease' },
						'& path:nth-child(3)': { opacity: 0 }
					}}
				>
					<svg viewBox={`0 0 ${width} ${height}`} fill="none">
						<defs>
							<pattern
								id={patterId}
								patternUnits="userSpaceOnUse"
								width={greaterThanLarge ? 18 : greaterThanMedium ? 13.5 : 9}
								height={greaterThanLarge ? 15 : greaterThanMedium ? 11.25 : 7.5}
								patternTransform="rotate(45)"
							>
								<line
									stroke="#ff8484"
									strokeWidth={greaterThanLarge ? 15 : greaterThanMedium ? 11.25 : 7.5}
									x1={0}
									y={0}
									x2={0}
									y2={greaterThanLarge ? 15 : greaterThanMedium ? 11.25 : 7.5}
								/>
							</pattern>
						</defs>

						<path fill="#192448" d={generatePath(0)} />
						<path fill={`url(#${patterId})`} d={generatePath(5)} />
					</svg>

					<Box
						display="flex"
						flexDirection="column"
						justifyContent="center"
						alignItems="center"
						width={1}
						height={1}
						py={2}
						px={4}
						position="absolute"
						top={0}
						left={0}
						textAlign="center"
						zIndex={1}
					>
						<Typography component="h3" fontSize={25} fontWeight={900} lineHeight={1.14}>
							{t(title)}
						</Typography>

						<Typography component="div" mt={2} fontSize={12} fontWeight={700} lineHeight={1.14}>
							{t(description)}
						</Typography>
					</Box>
				</Grid>
			</Grid>
		</Grid>
	)
}

export default CardMoelcule
